import { calculateMaxConsecutiveWins, calculateMaxConsecutiveLosses, runSeasonalityAnalysis } from './BacktestingUtils';

/**
 * Backtesting Engine - Logic for simulating strategy performance
 */

export const simulateAnimatedBacktest = async (context) => {
  const { 
    historicalData, initialCapital, advancedSettings, selectedStrategy, 
    selectedSymbol, timeframe, startDate, endDate, setProgress, 
    setCurrentBacktestIndex, setPatternAnalysis, buyMarkersRef, 
    sellMarkersRef, candleSeriesRef, riskPerTrade 
  } = context;

  const trades = [];
  let capital = initialCapital;
  let position = 0; // Units held
  let totalTrades = 0;
  let winningTradesCount = 0;
  let totalProfit = 0;
  let maxDrawdown = 0;
  let peakCapital = capital;
  let entryPrice = 0;
  let entryValue = 0;
  let totalCommission = 0;

  // Get strategy-specific parameters
  const stopLossPercent = advancedSettings.stopLoss;
  const takeProfitPercent = advancedSettings.takeProfit;
  const commissionRate = (advancedSettings.commission || 0) / 100;
  const slippageRate = (advancedSettings.slippage || 0) / 100;
  const maxTradesPossible = advancedSettings.maxTrades || 1000;
  const leverage = advancedSettings.leverage || 1;
  const allowLongTrading = advancedSettings.allowLongTrading !== false;
  const allowShortSelling = advancedSettings.allowShortSelling !== false;
  const strategyParams = advancedSettings.ema_crossover || advancedSettings.ma_crossover || {};
  const fastP = strategyParams.fastEMA || 9;
  const slowP = strategyParams.slowEMA || 21;
  const confirm = strategyParams.confirmCandles || 1;

  const prices = historicalData.map((candle) => parseFloat(candle.close));
  const highs = historicalData.map((candle) => parseFloat(candle.high) || parseFloat(candle.close));
  const lows = historicalData.map((candle) => parseFloat(candle.low) || parseFloat(candle.close));

  // Debug settings
  console.log(`[Backtest Start] Symbol: ${selectedSymbol}, SL: ${stopLossPercent}%, TP: ${takeProfitPercent}%, Fast: ${fastP}, Slow: ${slowP}`);

  // TRUE RECURSIVE EMA CALCULATION (Matches Delta Exchange Logic)
  const calculateEMAArray = (data, period) => {
    const k = 2 / (period + 1);
    const emaArray = new Array(data.length).fill(null);
    
    let sum = 0;
    for (let i = 0; i < period && i < data.length; i++) sum += data[i];
    let currentEma = sum / Math.min(period, data.length);
    if (period <= data.length) emaArray[period - 1] = currentEma;

    for (let i = period; i < data.length; i++) {
        currentEma = (data[i] * k) + (currentEma * (1 - k));
        emaArray[i] = currentEma;
    }
    return emaArray;
  };

  const fastEMAArray = calculateEMAArray(prices, fastP);
  const slowEMAArray = calculateEMAArray(prices, slowP);

  const getStrategySignal = (index) => {
    if (index < slowP + confirm + 2) return "HOLD";

    const fastNow = fastEMAArray[index];
    const slowNow = slowEMAArray[index];
    if (fastNow === null || slowNow === null) return "HOLD";

    const fastPrev = fastEMAArray[index - confirm];
    const slowPrev = slowEMAArray[index - confirm];

    if (fastPrev <= slowPrev && fastNow > slowNow) {
      for (let c = 0; c < confirm; c++) {
        if (fastEMAArray[index - c] <= slowEMAArray[index - c]) return "HOLD";
      }
      return "BUY";
    }

    if (fastPrev >= slowPrev && fastNow < slowNow) {
      for (let c = 0; c < confirm; c++) {
        if (fastEMAArray[index - c] >= slowEMAArray[index - c]) return "HOLD";
      }
      return "SELL";
    }

    return "HOLD";
  };

  const closePosition = (index, type, reason) => {
    let exitPrice = prices[index];
    
    // For SL/TP, use the target price instead of close for better accuracy
    if (reason === "STOP_LOSS") {
      const targetSL = position > 0 
        ? entryPrice * (1 - stopLossPercent / 100) 
        : entryPrice * (1 + stopLossPercent / 100);
      exitPrice = targetSL;
    } else if (reason === "TAKE_PROFIT") {
      const targetTP = position > 0 
        ? entryPrice * (1 + takeProfitPercent / 100) 
        : entryPrice * (1 - takeProfitPercent / 100);
      exitPrice = targetTP;
    } else {
      // For signals, add slippage
      exitPrice = type === "CLOSE_LONG" ? exitPrice * (1 - slippageRate) : exitPrice * (1 + slippageRate);
    }

    const exitValue = Math.abs(position) * exitPrice;
    const exitComm = exitValue * commissionRate;
    totalCommission += exitComm;
    
    let tradeProfit = 0;
    if (type === "CLOSE_LONG") {
      tradeProfit = (exitValue - entryValue) - (entryValue * commissionRate + exitComm);
    } else {
      tradeProfit = (entryValue - exitValue) - (entryValue * commissionRate + exitComm);
    }

    // Correct Capital Management for Leverage: 
    // Add net profit/loss to capital (instead of adding entire entryValue back)
    capital += tradeProfit; 
    
    totalProfit += tradeProfit;
    if (tradeProfit > 0) winningTradesCount++;

    trades.push({
      type,
      price: exitPrice,
      profit: tradeProfit,
      timestamp: historicalData[index].timestamp,
      reason,
      exitCommission: exitComm,
      indicatorValues: {
        fastEMA: fastEMAArray[index],
        slowEMA: slowEMAArray[index]
      },
      candle: historicalData[index]
    });

    position = 0;
    entryPrice = 0;
    entryValue = 0;
  };

  let lastClosedIndex = -1;

  for (let i = 50; i < prices.length && totalTrades < maxTradesPossible; i++) {
    const currentClose = prices[i];
    const currentHigh = highs[i];
    const currentLow = lows[i];

    // 1. Check Exit Conditions (SL/TP Priority) using High/Low
    if (position > 0) {
      const slPrice = entryPrice * (1 - stopLossPercent / 100);
      const tpPrice = entryPrice * (1 + takeProfitPercent / 100);
      
      if (currentLow <= slPrice) {
        closePosition(i, "CLOSE_LONG", "STOP_LOSS");
        lastClosedIndex = i;
      } else if (currentHigh >= tpPrice) {
        closePosition(i, "CLOSE_LONG", "TAKE_PROFIT");
        lastClosedIndex = i;
      }
    } else if (position < 0) {
      const slPrice = entryPrice * (1 + stopLossPercent / 100);
      const tpPrice = entryPrice * (1 - takeProfitPercent / 100);
      
      if (currentHigh >= slPrice) {
        closePosition(i, "CLOSE_SHORT", "STOP_LOSS");
        lastClosedIndex = i;
      } else if (currentLow <= tpPrice) {
        closePosition(i, "CLOSE_SHORT", "TAKE_PROFIT");
        lastClosedIndex = i;
      }
    }

    // 2. ONLY proceed to signals if no exit happened on this candle
    if (position === 0 && lastClosedIndex === i) continue;

    // 2b. Check Signal Flip (Strategy Override)
    let signal = getStrategySignal(i);
    if (!allowLongTrading && signal === "BUY") signal = "HOLD";
    if (!allowShortSelling && signal === "SELL") signal = "HOLD";

    // 3. Execute Signals
    if (signal === "BUY" && position <= 0 && totalTrades < maxTradesPossible) {
      if (position < 0) closePosition(i, "CLOSE_SHORT", "SIGNAL_FLIP");
      
      const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
      if (tradeAmount > 0 && capital > 0) {
        entryPrice = currentClose * (1 + slippageRate);
        entryValue = tradeAmount;
        const entryComm = entryValue * commissionRate;
        totalCommission += entryComm;
        position = entryValue / entryPrice;
        
        // Correct Capital management: Only subtract commission at entry
        capital -= entryComm;
        
        trades.push({
          type: "OPEN_LONG",
          price: entryPrice,
          amount: position,
          timestamp: historicalData[i].timestamp,
          commission: entryComm,
          indicatorValues: {
            fastEMA: fastEMAArray[i],
            slowEMA: slowEMAArray[i]
          },
          candle: historicalData[i]
        });
        totalTrades++;
      }
    } else if (signal === "SELL" && position >= 0 && totalTrades < maxTradesPossible) {
      if (position > 0) closePosition(i, "CLOSE_LONG", "SIGNAL_FLIP");

      const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
      if (tradeAmount > 0 && capital > 0) {
        entryPrice = currentClose * (1 - slippageRate);
        entryValue = tradeAmount;
        const entryComm = entryValue * commissionRate;
        totalCommission += entryComm;
        position = -(entryValue / entryPrice);
        
        // Correct Capital management: Only subtract commission at entry
        capital -= entryComm;

        trades.push({
          type: "OPEN_SHORT",
          price: entryPrice,
          amount: Math.abs(position),
          timestamp: historicalData[i].timestamp,
          commission: entryComm,
          indicatorValues: {
            fastEMA: fastEMAArray[i],
            slowEMA: slowEMAArray[i]
          },
          candle: historicalData[i]
        });
        totalTrades++;
      }
    }

    // Tracking
    if (capital > peakCapital) peakCapital = capital;
    maxDrawdown = Math.max(maxDrawdown, (peakCapital - capital) / peakCapital);
    
    if (setCurrentBacktestIndex) setCurrentBacktestIndex(i);
    if (setProgress) setProgress((i / prices.length) * 100);
    if (i % 50 === 0) await new Promise(r => setTimeout(r, 0));
  }

  const completedTrades = trades.filter(t => t.type.startsWith("CLOSE"));
  const actualTotalTrades = completedTrades.length;
  
  const wins = completedTrades.filter(t => t.profit > 0);
  const losses = completedTrades.filter(t => t.profit <= 0);
  
  const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.profit, 0) / wins.length : 0;
  const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((s, t) => s + t.profit, 0) / losses.length) : 0;
  const totalGrossProfit = wins.reduce((s, t) => s + t.profit, 0);
  const totalGrossLoss = Math.abs(losses.reduce((s, t) => s + t.profit, 0));
  const profitFactor = totalGrossLoss > 0 ? totalGrossProfit / totalGrossLoss : totalGrossProfit;

  const seasonality = runSeasonalityAnalysis(trades);
  if (setPatternAnalysis) setPatternAnalysis(seasonality);

  return {
    totalTrades: actualTotalTrades,
    winningTrades: wins.length,
    winRate: actualTotalTrades > 0 ? (wins.length / actualTotalTrades) * 100 : 0,
    totalReturn: ((capital - initialCapital) / initialCapital) * 100,
    finalCapital: capital,
    maxDrawdown: maxDrawdown * 100,
    netProfit: capital - initialCapital,
    avgWin,
    avgLoss,
    profitFactor,
    totalCommission,
    trades,
    initialCapital,
    seasonality
  };
};

export const simulateFastBacktest = (context) => {
  const { 
    historicalData, initialCapital, advancedSettings, riskPerTrade, leverage = 1
  } = context;

  if (!historicalData || historicalData.length === 0) return { totalTrades: 0, winRate: 0, totalReturn: 0, netProfit: 0, maxDrawdown: 0, profitFactor: 0 };

  const trades = [];
  let capital = initialCapital;
  let position = 0; 
  let totalTrades = 0;
  let peakCapital = capital;
  let entryPrice = 0;
  let entryValue = 0;

  const stopLossPercent = advancedSettings.stopLoss;
  const takeProfitPercent = advancedSettings.takeProfit;
  const commissionRate = (advancedSettings.commission || 0) / 100;
  const slippageRate = (advancedSettings.slippage || 0) / 100;
  
  const strategyParams = advancedSettings.ema_crossover || advancedSettings.ma_crossover || {};
  const fastP = strategyParams.fastEMA || 9;
  const slowP = strategyParams.slowEMA || 21;
  const confirm = strategyParams.confirmCandles || 1;

  const prices = historicalData.map((candle) => parseFloat(candle.close));
  const highs = historicalData.map((candle) => parseFloat(candle.high) || parseFloat(candle.close));
  const lows = historicalData.map((candle) => parseFloat(candle.low) || parseFloat(candle.close));

  const calculateEMAArray = (data, period) => {
    const k = 2 / (period + 1);
    const emaArray = new Array(data.length).fill(null);
    let sum = 0;
    for (let i = 0; i < period && i < data.length; i++) sum += data[i];
    let currentEma = sum / Math.min(period, data.length);
    if (period <= data.length) emaArray[period - 1] = currentEma;
    for (let i = period; i < data.length; i++) {
        currentEma = (data[i] * k) + (currentEma * (1 - k));
        emaArray[i] = currentEma;
    }
    return emaArray;
  };

  const fastEMAArray = calculateEMAArray(prices, fastP);
  const slowEMAArray = calculateEMAArray(prices, slowP);

  const getStrategySignal = (i) => {
    if (i < slowP + confirm + 2) return "HOLD";
    const fastNow = fastEMAArray[i];
    const slowNow = slowEMAArray[i];
    if (fastNow === null || slowNow === null) return "HOLD";
    const fastPrev = fastEMAArray[i - confirm];
    const slowPrev = slowEMAArray[i - confirm];
    if (fastPrev <= slowPrev && fastNow > slowNow) return "BUY";
    if (fastPrev >= slowPrev && fastNow < slowNow) return "SELL";
    return "HOLD";
  };

  let maxDrawdown = 0;

  for (let i = slowP + 5; i < prices.length; i++) {
    if (position > 0) {
      if (lows[i] <= entryPrice * (1 - stopLossPercent / 100)) {
        const exitPrice = entryPrice * (1 - stopLossPercent / 100);
        const exitValue = Math.abs(position) * exitPrice;
        const netProfit = (exitValue - entryValue) - (entryValue * commissionRate + exitValue * commissionRate);
        capital += netProfit;
        trades.push({ type: "CLOSE_LONG", profit: netProfit });
        position = 0;
      } else if (highs[i] >= entryPrice * (1 + takeProfitPercent / 100)) {
        const exitPrice = entryPrice * (1 + takeProfitPercent / 100);
        const exitValue = Math.abs(position) * exitPrice;
        const netProfit = (exitValue - entryValue) - (entryValue * commissionRate + exitValue * commissionRate);
        capital += netProfit;
        trades.push({ type: "CLOSE_LONG", profit: netProfit });
        position = 0;
      }
    } else if (position < 0) {
       if (highs[i] >= entryPrice * (1 + stopLossPercent / 100)) {
        const exitPrice = entryPrice * (1 + stopLossPercent / 100);
        const exitValue = Math.abs(position) * exitPrice;
        const netProfit = (entryValue - exitValue) - (entryValue * commissionRate + exitValue * commissionRate);
        capital += netProfit;
        trades.push({ type: "CLOSE_SHORT", profit: netProfit });
        position = 0;
      } else if (lows[i] <= entryPrice * (1 - takeProfitPercent / 100)) {
        const exitPrice = entryPrice * (1 - takeProfitPercent / 100);
        const exitValue = Math.abs(position) * exitPrice;
        const netProfit = (entryValue - exitValue) - (entryValue * commissionRate + exitValue * commissionRate);
        capital += netProfit;
        trades.push({ type: "CLOSE_SHORT", profit: netProfit });
        position = 0;
      }
    }

    if (position === 0) {
      const signal = getStrategySignal(i);
      if (signal === "BUY") {
        const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
        entryPrice = prices[i] * (1 + slippageRate);
        entryValue = tradeAmount;
        position = entryValue / entryPrice;
        capital -= tradeAmount * commissionRate;
        totalTrades++;
      } else if (signal === "SELL") {
        const tradeAmount = ((capital * riskPerTrade) / 100) * leverage;
        entryPrice = prices[i] * (1 - slippageRate);
        entryValue = tradeAmount;
        position = -(entryValue / entryPrice);
        capital -= tradeAmount * commissionRate;
        totalTrades++;
      }
    }

    if (capital > peakCapital) peakCapital = capital;
    maxDrawdown = Math.max(maxDrawdown, (peakCapital - capital) / peakCapital);
  }

  const completedTrades = trades.filter(t => t.type.startsWith("CLOSE"));
  const wins = completedTrades.filter(t => t.profit > 0);
  const losses = completedTrades.filter(t => t.profit <= 0);
  const totalGrossProfit = wins.reduce((s, t) => s + t.profit, 0);
  const totalGrossLoss = Math.abs(losses.reduce((s, t) => s + t.profit, 0));

  return {
    totalTrades: completedTrades.length,
    winRate: completedTrades.length > 0 ? (wins.length / completedTrades.length) * 100 : 0,
    totalReturn: ((capital - initialCapital) / initialCapital) * 100,
    netProfit: capital - initialCapital,
    maxDrawdown: maxDrawdown * 100,
    profitFactor: totalGrossLoss > 0 ? totalGrossProfit / totalGrossLoss : totalGrossProfit
  };
};

export const simulateBacktest = async (context) => {
  return simulateAnimatedBacktest(context);
};